import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

interface CertRow {
  classId: string;
  classid: number;
  classname: string;
  s3Key: string;
  originalName: string;
  mimeType?: string;
  storageType: 'S3' | 'local';
  uploadedAt?: string;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  logId?: string;
}

interface DeletedCertRow {
  _id: string;
  classId: string;
  classid: number;
  classname: string;
  s3Key: string;
  originalName: string;
  mimeType?: string;
  storageType: 'S3' | 'local';
  uploadedAt?: string;
  deletedAt?: string;
  deletedBy?: string;
  deleteReason?: string;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
}

interface PermDeleteTarget {
  logId: string;
  label: string;
}

const fmtDate = (val: string | undefined) => {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d.getTime())) return val;
  return `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}/${d.getUTCFullYear()}`;
};

const fileTypeIcon = (mimeType?: string, originalName?: string) => {
  const name = originalName?.toLowerCase() ?? '';
  if (mimeType === 'application/pdf' || name.endsWith('.pdf')) return '📄';
  if (mimeType?.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/.test(name)) return '🖼';
  if (/\.(doc|docx)$/.test(name)) return '📝';
  return '📎';
};

const fileTypeBadge = (mimeType?: string, originalName?: string) => {
  const name = originalName?.toLowerCase() ?? '';
  if (mimeType === 'application/pdf' || name.endsWith('.pdf')) return 'PDF';
  if (mimeType?.startsWith('image/')) return mimeType.split('/')[1].toUpperCase();
  if (/\.(jpg|jpeg)$/.test(name)) return 'JPEG';
  if (/\.png$/.test(name)) return 'PNG';
  if (/\.(doc|docx)$/.test(name)) return 'DOCX';
  return 'FILE';
};

const AdminCertificates: React.FC = () => {
  const navigate = useNavigate();

  const [active, setActive] = useState<CertRow[]>([]);
  const [deleted, setDeleted] = useState<DeletedCertRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Search / filter
  const [searchActive, setSearchActive] = useState('');
  const [searchDeleted, setSearchDeleted] = useState('');

  // Preview
  const [previewing, setPreviewing] = useState<string | null>(null);

  // Restore
  const [restoring, setRestoring] = useState<string | null>(null);

  // Permanent delete modal
  const [permTarget, setPermTarget] = useState<PermDeleteTarget | null>(null);
  const [permPhrase, setPermPhrase] = useState('');
  const [permPw, setPermPw] = useState('');
  const [permDeleting, setPermDeleting] = useState(false);
  const [permError, setPermError] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [activeRes, deletedRes] = await Promise.all([
        api.get('/api/certificates/admin/active'),
        api.get('/api/certificates/admin/deleted'),
      ]);
      setActive(activeRes.data ?? []);
      setDeleted(deletedRes.data ?? []);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load certificate data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const payload = JSON.parse(atob(token.split('.')[1]));
      const adminId = payload?.user?.id;
      if (adminId) {
        api.get(`/api/users/${adminId}`)
          .then(res => setIsSuperAdmin(res.data?.isSuperAdmin === true))
          .catch(() => {});
      }
    } catch {}
  }, []);

  const handlePreview = async (logId: string | undefined, classId?: string) => {
    if (!logId && !classId) return;
    setPreviewing(logId ?? classId ?? null);
    try {
      // Active cert: use class certificate endpoint; deleted cert: use log preview endpoint
      const endpoint = logId
        ? `/api/certificates/admin/${logId}/preview`
        : `/api/classes/${classId}/certificate`;
      const res = await api.get(endpoint);
      const url = res.data?.url;
      if (url) {
        window.open(url.startsWith('http') ? url : `${window.location.origin}${url}`, '_blank');
      }
    } catch {
      alert('Could not load preview.');
    } finally {
      setPreviewing(null);
    }
  };

  const handleRestore = async (logId: string) => {
    setRestoring(logId);
    try {
      await api.put(`/api/certificates/admin/${logId}/restore`);
      await loadData();
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Restore failed.');
    } finally {
      setRestoring(null);
    }
  };

  const openPermDelete = (logId: string, label: string) => {
    setPermTarget({ logId, label });
    setPermPhrase('');
    setPermPw('');
    setPermError('');
  };

  const closePermDelete = () => {
    setPermTarget(null);
    setPermPhrase('');
    setPermPw('');
    setPermError('');
  };

  const handlePermDelete = async () => {
    if (!permTarget) return;
    setPermDeleting(true);
    setPermError('');
    try {
      await api.delete(`/api/certificates/admin/permanent/${permTarget.logId}`, {
        data: { confirmPhrase: permPhrase, adminPassword: permPw },
      });
      setDeleted(prev => prev.filter(d => d._id !== permTarget.logId));
      closePermDelete();
    } catch (err: any) {
      setPermError(err?.response?.data?.error || 'Permanent delete failed.');
    } finally {
      setPermDeleting(false);
    }
  };

  const filteredActive = active.filter(r => {
    const q = searchActive.toLowerCase();
    return !q || r.classname?.toLowerCase().includes(q) || r.userEmail?.toLowerCase().includes(q) || r.originalName?.toLowerCase().includes(q);
  });

  const filteredDeleted = deleted.filter(r => {
    const q = searchDeleted.toLowerCase();
    return !q || r.classname?.toLowerCase().includes(q) || r.userEmail?.toLowerCase().includes(q) || r.originalName?.toLowerCase().includes(q);
  });

  return (
    <div className="container-fluid mt-4 px-4" style={{ maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-2">
        <div>
          <button className="btn btn-sm btn-outline-secondary mb-2" onClick={() => navigate(-1)}>← Back</button>
          <h3 className="mb-0">Certificate Management</h3>
          <p className="text-muted small mb-0">
            Sitewide view of all uploaded certificates. Deleted files are retained in storage until permanently removed.
          </p>
        </div>
        <button className="btn btn-sm btn-outline-secondary" onClick={loadData} disabled={loading}>
          {loading ? '…' : '↺ Refresh'}
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status" />
          <p className="mt-3 text-muted">Loading certificates…</p>
        </div>
      ) : (
        <>
          {/* ── Active Certificates ── */}
          <div className="card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center py-2">
              <div>
                <span className="fw-semibold">Active Certificates</span>
                <span className="badge bg-success ms-2">{active.length}</span>
              </div>
              <input
                className="form-control form-control-sm"
                style={{ width: 240 }}
                placeholder="Search class, user, filename…"
                value={searchActive}
                onChange={e => setSearchActive(e.target.value)}
              />
            </div>
            <div className="card-body p-0">
              {filteredActive.length === 0 ? (
                <p className="text-muted p-3 mb-0">No active certificates found.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-sm mb-0" style={{ fontSize: '0.83rem' }}>
                    <thead className="table-light">
                      <tr>
                        <th>Type</th>
                        <th>File</th>
                        <th>Course</th>
                        <th>User</th>
                        <th>Storage</th>
                        <th>Uploaded</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredActive.map(row => (
                        <tr key={row.classId}>
                          <td>
                            <span title={row.mimeType}>
                              {fileTypeIcon(row.mimeType, row.originalName)}{' '}
                              <span className="badge bg-light text-dark border" style={{ fontSize: '0.7rem' }}>
                                {fileTypeBadge(row.mimeType, row.originalName)}
                              </span>
                            </span>
                          </td>
                          <td style={{ maxWidth: 200 }}>
                            <div className="text-truncate" title={row.originalName}>{row.originalName || '—'}</div>
                          </td>
                          <td style={{ maxWidth: 220 }}>
                            <div className="text-truncate" title={row.classname}>{row.classname}</div>
                            <small className="text-muted">#{row.classid}</small>
                          </td>
                          <td>
                            <div>{row.userEmail}</div>
                            {(row.userFirstName || row.userLastName) && (
                              <small className="text-muted">{row.userFirstName} {row.userLastName}</small>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${row.storageType === 'S3' ? 'bg-primary' : 'bg-secondary'}`}>
                              {row.storageType}
                            </span>
                          </td>
                          <td>{fmtDate(row.uploadedAt)}</td>
                          <td className="text-end">
                            <button
                              className="btn btn-sm btn-outline-info py-0 px-2"
                              disabled={previewing === (row.logId ?? row.classId)}
                              onClick={() => handlePreview(row.logId ?? undefined, row.classId)}
                            >
                              {previewing === (row.logId ?? row.classId) ? '…' : 'View'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* ── Soft-Deleted Certificates ── */}
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center py-2">
              <div>
                <span className="fw-semibold text-danger">Soft-Deleted Certificates</span>
                <span className="badge bg-danger ms-2">{deleted.length}</span>
                <small className="text-muted ms-2">Files still in storage — recoverable until permanently removed</small>
              </div>
              <input
                className="form-control form-control-sm"
                style={{ width: 240 }}
                placeholder="Search class, user, filename…"
                value={searchDeleted}
                onChange={e => setSearchDeleted(e.target.value)}
              />
            </div>
            <div className="card-body p-0">
              {filteredDeleted.length === 0 ? (
                <p className="text-muted p-3 mb-0">No soft-deleted certificates.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0" style={{ fontSize: '0.83rem' }}>
                    <thead className="table-light">
                      <tr>
                        <th>Type</th>
                        <th>File</th>
                        <th>Course</th>
                        <th>User</th>
                        <th>Reason</th>
                        <th>Deleted</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDeleted.map(row => (
                        <tr key={row._id} className="table-danger" style={{ opacity: 0.9 }}>
                          <td>
                            <span title={row.mimeType}>
                              {fileTypeIcon(row.mimeType, row.originalName)}{' '}
                              <span className="badge bg-light text-dark border" style={{ fontSize: '0.7rem' }}>
                                {fileTypeBadge(row.mimeType, row.originalName)}
                              </span>
                            </span>
                          </td>
                          <td style={{ maxWidth: 200 }}>
                            <div className="text-truncate" title={row.originalName}>{row.originalName || '—'}</div>
                          </td>
                          <td style={{ maxWidth: 200 }}>
                            <div className="text-truncate" title={row.classname}>{row.classname || '—'}</div>
                            {row.classid && <small className="text-muted">#{row.classid}</small>}
                          </td>
                          <td>
                            <div>{row.userEmail}</div>
                            {(row.userFirstName || row.userLastName) && (
                              <small className="text-muted">{row.userFirstName} {row.userLastName}</small>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${
                              row.deleteReason === 'replaced' ? 'bg-warning text-dark' :
                              row.deleteReason === 'admin_deleted' ? 'bg-dark' : 'bg-secondary'
                            }`} style={{ fontSize: '0.7rem' }}>
                              {row.deleteReason === 'replaced' ? 'Replaced' :
                               row.deleteReason === 'admin_deleted' ? 'Admin' : 'User'}
                            </span>
                          </td>
                          <td>{fmtDate(row.deletedAt)}</td>
                          <td>
                            <div className="d-flex gap-1 flex-wrap">
                              <button
                                className="btn btn-sm btn-outline-secondary py-0 px-2"
                                disabled={previewing === row._id}
                                onClick={() => handlePreview(row._id)}
                              >
                                {previewing === row._id ? '…' : 'View'}
                              </button>
                              {row.deleteReason !== 'replaced' && (
                                <button
                                  className="btn btn-sm btn-outline-success py-0 px-2"
                                  disabled={restoring === row._id}
                                  onClick={() => handleRestore(row._id)}
                                >
                                  {restoring === row._id ? '…' : 'Restore'}
                                </button>
                              )}
                              {isSuperAdmin && (
                                <button
                                  className="btn btn-sm btn-danger py-0 px-2"
                                  onClick={() => openPermDelete(
                                    row._id,
                                    `${row.classname || 'Unknown'} — ${row.originalName || row.s3Key}`
                                  )}
                                >
                                  Perm. Remove
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Permanent Delete Modal ── */}
      {permTarget && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.65)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-danger">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">⚠ Permanently Remove Certificate</h5>
                <button className="btn-close btn-close-white" onClick={closePermDelete} disabled={permDeleting} />
              </div>
              <div className="modal-body">
                <p className="mb-2">You are about to <strong>permanently and irreversibly delete</strong> the file from storage:</p>
                <div className="alert alert-danger py-2 mb-3" style={{ wordBreak: 'break-all', fontSize: '0.85rem' }}>
                  {permTarget.label}
                </div>
                <p className="text-muted small mb-3">
                  The S3 object (or local file) will be deleted. This cannot be undone. An audit log entry will be created.
                </p>
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Type <code>PERMANENTLY DELETE</code> to confirm:
                  </label>
                  <input
                    className="form-control"
                    value={permPhrase}
                    onChange={e => setPermPhrase(e.target.value)}
                    placeholder="PERMANENTLY DELETE"
                    autoFocus
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Your admin password:</label>
                  <input
                    className="form-control"
                    type="password"
                    value={permPw}
                    onChange={e => setPermPw(e.target.value)}
                    placeholder="Enter your admin password"
                  />
                </div>
                {permError && <div className="alert alert-danger py-2">{permError}</div>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closePermDelete} disabled={permDeleting}>Cancel</button>
                <button
                  className="btn btn-danger"
                  onClick={handlePermDelete}
                  disabled={permPhrase !== 'PERMANENTLY DELETE' || !permPw.trim() || permDeleting}
                >
                  {permDeleting ? 'Deleting…' : 'Permanently Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCertificates;
